package fr.frux.core.maps

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.calculateEndPadding
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.union
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import fr.frux.core.common.logging.Logger
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put
import org.maplibre.compose.camera.CameraMoveReason
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.expressions.dsl.Feature
import org.maplibre.compose.expressions.dsl.asBoolean
import org.maplibre.compose.expressions.dsl.asNumber
import org.maplibre.compose.expressions.dsl.condition
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.convertToString
import org.maplibre.compose.expressions.dsl.eq
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.dsl.times
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.layers.CircleLayer
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.map.LocalMapState
import org.maplibre.compose.map.MapEvent
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.map.StyleLoadState
import org.maplibre.compose.map.rememberMapState
import org.maplibre.compose.overlay.AttributionLinks
import org.maplibre.compose.overlay.ExpandingAttributionButton
import org.maplibre.compose.overlay.MaplibreWebsiteUrl
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.GeoJsonOptions
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.spatialk.geojson.FeatureCollection
import org.maplibre.spatialk.geojson.Point
import org.maplibre.spatialk.geojson.Position
import kotlin.time.Duration.Companion.milliseconds
import androidx.compose.foundation.layout.offset as offsetModifier
import org.maplibre.spatialk.geojson.Feature as GeoJsonFeature

/** Duree d'un deplacement de camera demande par l'application. */
private val MOVE_DURATION = 400.milliseconds

internal const val MARKER_ID_PROPERTY = "markerId"
internal const val MARKER_STYLE_PROPERTY = "style"
internal const val MARKER_ALPHA_PROPERTY = "alpha"
private const val MARKER_SELECTED_PROPERTY = "selected"
private const val POINT_COUNT_PROPERTY = "point_count"

/** Aucune cle de style ne vaut cette valeur : ramene systematiquement au repli du [switch]. */
internal const val DEFAULT_STYLE_KEY = ""

private const val MARKERS_LAYER_ID = "frux-trees"
private const val CLUSTER_LAYER_ID = "frux-trees-cluster"
private const val USER_LOCATION_LAYER_ID = "frux-user-location"

/**
 * Rayon de regroupement, en pixels. Releve de 50 a 90 avec les satellites d'especes (#710) : une
 * couronne s'etend a 46 dp du centre, et a 50 les couronnes voisines se melaient au point de ne
 * plus dire a quel compteur appartenait un satellite. Le prix est assume : les arbres se regroupent
 * plus tot, un cluster entoure de ses especes en disant plus que trois marqueurs serres.
 *
 * Duplique dans `ClusterSettings` (backend), a changer des deux cotes.
 */
private const val CLUSTER_RADIUS = 90

/**
 * Trois arbres au moins pour former un regroupement (#710, 2 avant). Une paire montre ses deux
 * marqueurs, qui portent espece et etat, plutot qu'une pastille « 2 » qui n'en dit que l'espece.
 * Prix assume, a revoir s'il gene : deux arbres plantes cote a cote se superposent jusqu'au zoom
 * maximal (ce que #398 evitait), le toucher ouvrant alors la feuille de selection des deux.
 *
 * Duplique dans `ClusterSettings` (backend), a changer des deux cotes.
 */
private const val CLUSTER_MIN_POINTS = 3

/**
 * Dernier zoom entier ou la source regroupe encore (#398) : le zoom maximal de la carte, et non
 * plus 14. Le rayon de regroupement est en pixels, donc a mesure que l'on zoome il ne couvre plus
 * que les marqueurs qui se recouvriraient a l'ecran : au zoom 20, environ quatre metres. Deux
 * arbres plantes cote a cote restent une pastille comptee jusqu'au bout, jamais deux epingles
 * empilees dont une seule se laisse toucher.
 *
 * Duplique dans `ClusterSettings` (backend), a changer des deux cotes.
 */
internal val CLUSTER_MAX_ZOOM: Int = MAX_MAP_ZOOM.toInt()

internal val USER_LOCATION_COLOR = Color(0xFF1A73E8)

/**
 * Credit du moteur de rendu dans le panneau d'attribution, a la place du wordmark que le SDK dessine
 * par defaut. Courtoisie, pas obligation : le nom suffit, et il reste un lien.
 */
private const val MAPLIBRE_ATTRIBUTION = "<a href=\"$MaplibreWebsiteUrl\">MapLibre</a>"

/** Vert feuillage du logo, distinct des teintes de statut des marqueurs individuels. */
internal val CLUSTER_COLOR = Color(0xFF223F2B)
internal val CLUSTER_TEXT_COLOR = Color(0xFFFAF8F2)

/** Doit figurer parmi les polices publiées par workers/tiles (scripts/fetch-assets.mjs). */
internal const val CLUSTER_FONT = "Roboto Medium"

// MapLibre centre la boite de ligne de la police, jambages compris : des chiffres, qui n'en ont
// pas, ressortent trop haut dans la pastille. En em pour suivre la taille du texte.
internal const val CLUSTER_TEXT_BASELINE_SHIFT_EM = 0.1f

/** Blanc franc sur la pastille : le creme d'avant servait la silhouette, pas un disque plein. */
internal val GLYPH_COLOR = Color.White

/**
 * Carte reelle via maplibre-compose (#51), commune a toutes les plateformes qui l'implementent
 * (android/ios/js — chacune delegue depuis son actual `FruxMap`).
 *
 * C'est ici, et nulle part ailleurs, que les types MapLibre apparaissent — garde-fou n°1 de
 * l'ADR 0001, qui garde l'essai reversible au prix d'un fichier par plateforme delegant vers
 * celui-ci.
 *
 * Le style reste celui du fournisseur (garde-fou n°2) : pas de theme « nature » vectoriel.
 *
 * Les marqueurs passent par une source GeoJSON + une couche de symboles plutot que par des
 * composables superposes : c'est le SDK qui les positionne pendant les gestes, sans recomposition
 * par arbre a chaque frame. Depuis maplibre-compose 0.16, les quatre cibles savent le faire :
 * la surcouche Compose qui suppleait la cible js n'a plus de raison d'exister.
 */
@Composable
internal fun MapLibreFruxMap(
    camera: MapCameraState,
    markers: List<MapMarker>,
    userLocation: Location?,
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
    onMarkerClick: (MarkerId) -> Unit,
    onMarkerGroupClick: (List<MarkerId>) -> Unit,
    plaques: List<MapPlaque>,
    onPlaqueClick: (PlaqueId) -> Unit,
    showOrnaments: Boolean,
    attributions: List<String>,
    markerCallout: (@Composable (MarkerId) -> Unit)?,
    onUserGesture: () -> Unit,
    pyramid: MapPyramid?,
    pyramidOpacity: Float,
    onPyramidTreeClick: (MarkerId) -> Unit,
    modifier: Modifier,
) {
    // Sans pyramide, rien a fondre : le lot detaille reste pleinement visible quoi que dise
    // l'appelant.
    val shownPyramidOpacity = if (pyramid == null) 0f else pyramidOpacity.coerceIn(0f, 1f)
    val detailOpacity = 1f - shownPyramidOpacity
    val detailTappable = detailOpacity >= TAPPABLE_LAYER_OPACITY

    // Taille de la vue, necessaire pour traduire un cadrage en zoom : c'est la seule couche qui la
    // connaisse.
    var viewportSize by remember { mutableStateOf(DpSize.Zero) }
    val layoutDirection = LocalLayoutDirection.current

    // Cadrage en cours. Garde en etat plutot qu'applique une fois pour toutes : les surcouches de
    // l'ecran (barre « a proximite », bandeau de localisation) apparaissent dans la meme frame que
    // le lot d'arbres qui declenche le cadrage, donc la premiere mesure de leurs marges arrive
    // apres la demande. Le garder permet de le rejouer sur la vue reellement libre.
    var pendingFit by remember { mutableStateOf<MapBounds?>(null) }
    var animateFit by remember { mutableStateOf(true) }

    // Un chargement rate (tuiles injoignables, cle invalide) laisse une vue vide : la seule facon
    // de relancer le chargement du style est de recreer la carte, d'ou la cle d'essai.
    var attempt by remember { mutableIntStateOf(0) }
    var loadFailure by remember { mutableStateOf<String?>(null) }

    // Vue au moment du reessai. La carte recreee repart de sa position initiale : sans ce report,
    // un reessai ramenerait l'utilisateur la ou la carte avait ete ouverte, pas la ou il regarde.
    var retryFrom by remember { mutableStateOf<MapCameraPosition?>(null) }

    val density = LocalDensity.current
    // Voile affiché à la place de la carte avant son premier rendu (cf. mapUiOptions) : le fond du
    // thème, pour traverser les transitions d'écran sans éclair noir ni blanc.
    val loadColor = MaterialTheme.colorScheme.surface

    // Callbacks relus a l'appel et non captures dans la configuration : `MapInteractions` se
    // compare par egalite de structure, et une lambda recreee a chaque recomposition ferait
    // reconfigurer l'entree de la carte pour rien.
    val currentOnUserGesture by rememberUpdatedState(onUserGesture)
    val interactions =
        remember {
            MapInteractions {
                camera {
                    // Ni rotation ni inclinaison : la carte reste au nord, comme sur le web depuis
                    // toujours (ADR 0007). Une pastille de cueillette n'a rien a gagner d'une vue
                    // oblique, et la boussole qui servait a revenir au nord disparait avec.
                    rotate { enabled = false }
                    tilt { enabled = false }
                }
                callbacks {
                    click {
                        // Clic qu'aucune couche n'a consomme : depuis 0.16 le SDK interroge
                        // d'abord les couches (marqueurs, clusters) et n'appelle ce callback
                        // qu'ensuite. Les branches consommees signalent donc le geste
                        // elles-memes, sinon un tap sur marqueur ne fermerait plus le clavier
                        // de recherche.
                        onEvent {
                            currentOnUserGesture()
                            ClickResult.Pass
                        }
                    }
                }
            }
        }

    Box(
        modifier =
            modifier
                .fillMaxSize()
                .onSizeChanged { size ->
                    viewportSize = with(density) { DpSize(size.width.toDp(), size.height.toDp()) }
                },
    ) {
        key(attempt) {
            val mapState =
                rememberMapState(
                    baseStyle = BaseStyle.Uri(mapStyleUrl()),
                    // `initialPosition` et non `position` : lire l'etat observable ici abonnerait
                    // la composition de la carte a chaque frame de pan (cf.
                    // MapCameraState.initialPosition). `retryFrom` n'est lu qu'apres un reessai,
                    // ou il est fige (cf. onRetry).
                    initialCameraPosition = (retryFrom ?: camera.initialPosition).toMaplibre(),
                ) {
                    // Etat de la carte dont ce style est le contenu. Lu ici pour etre relu dans les
                    // callbacks de couche seulement : lire `cameraPosition` en composition
                    // recomposerait tout le style a chaque frame de pan.
                    val styleMapState = LocalMapState.current
                    // Le zoom d'eclatement et les feuilles d'un regroupement se demandent a la
                    // source en suspendant : le callback de couche, lui, ne suspend pas.
                    val clusterTapScope = rememberCoroutineScope()

                    val imageCache = rememberMarkerImageCache(markerIcons, fallbackGlyph)
                    val clusterDiscPainter = rememberClusterDiscPainter()

                    // Badges des satellites (#710). Le registre ne retrecit pas : ses agregats sont des
                    // options de la source, qui se recree quand elles changent.
                    val badgeRegistry = remember { BadgeRegistry() }
                    val badges = remember(markers) { badgeRegistry.register(markers) }
                    val clusterProperties = remember(badges.size) { badgeClusterProperties(badges.size) }
                    val markerSource =
                        rememberGeoJsonSource(
                            data =
                                GeoJsonData.Features(
                                    remember(markers, badges) { markers.toFeatureCollection(badgeRegistry) },
                                ),
                            options =
                                GeoJsonOptions(
                                    // La pyramide de tuiles de la source s'arrete a 18 par defaut :
                                    // au-dela, la carte etirerait les regroupements calcules a 18,
                                    // et deux arbres a quinze metres resteraient une pastille au
                                    // zoom 20. Alignee sur le zoom maximal pour que le rayon en
                                    // pixels garde son sens jusqu'au bout.
                                    maxZoom = CLUSTER_MAX_ZOOM,
                                    cluster = true,
                                    clusterRadius = CLUSTER_RADIUS,
                                    clusterMinPoints = CLUSTER_MIN_POINTS,
                                    clusterMaxZoom = CLUSTER_MAX_ZOOM,
                                    clusterProperties = clusterProperties,
                                ),
                        )
                    // Une seule couche de symboles pour tout le marqueur (#269) : bord, plaque et
                    // glyphe en trois couches se rendaient par etage (tous les bords, puis toutes
                    // les plaques, puis tous les glyphes), et la plaque d'un marqueur voisin
                    // s'intercalait entre la plaque d'un arbre et son glyphe. L'icone est donc
                    // pre-composee par combinaison visible (CompositeMarkerPainter) et choisie d'un
                    // bloc par feature via sa cle de style. Moins de couches, c'est aussi moins
                    // cher a rendre.
                    val markerImages = remember(markers, imageCache) { imageCache.imagesFor(markers) }

                    if (markerImages.isNotEmpty()) {
                        // Un `switch` par taille : la selection ne fait pas etirer le raster du
                        // repos (flou bilineaire, cf. SELECTED_COMPOSITE_MARKER_SIZE), elle en
                        // choisit un second dessine a la taille agrandie, enregistre pour la
                        // seule cle du marqueur selectionne.
                        fun imagesBySize(
                            keys: Collection<String>,
                            side: Dp,
                        ) = markerImageBySize(markerImages, keys, side, imageCache.disc)
                        val selectedKeys = remember(markers) { selectedStyleKeys(markers) }
                        SymbolLayer(
                            id = MARKERS_LAYER_ID,
                            source = markerSource,
                            filter = Feature.has(POINT_COUNT_PROPERTY).eq(const(false)),
                            iconImage =
                                if (selectedKeys.isEmpty()) {
                                    imagesBySize(markerImages.keys, COMPOSITE_MARKER_SIZE)
                                } else {
                                    switch(
                                        condition(
                                            test = Feature[MARKER_SELECTED_PROPERTY].asBoolean(const(false)),
                                            output = imagesBySize(selectedKeys, SELECTED_COMPOSITE_MARKER_SIZE),
                                        ),
                                        fallback = imagesBySize(markerImages.keys, COMPOSITE_MARKER_SIZE),
                                    )
                                },
                            // Repli explicite : les conversions d'expression de 0.16 acceptent un
                            // attribut absent, et sans repli une feature sans `alpha` rendrait une
                            // icone invisible plutot qu'opaque.
                            iconOpacity =
                                Feature[MARKER_ALPHA_PROPERTY].asNumber(const(1f)).let { alpha ->
                                    if (detailOpacity < 1f) alpha * const(detailOpacity) else alpha
                                },
                            // Sans ca, MapLibre masque les symboles qui se chevauchent : des arbres
                            // disparaitraient en zone dense.
                            iconAllowOverlap = const(true),
                            onClick = { features ->
                                val clicked =
                                    features
                                        .mapNotNull { feature ->
                                            feature.properties
                                                ?.get(MARKER_ID_PROPERTY)
                                                ?.jsonPrimitive
                                                ?.longOrNull
                                                ?.let(::MarkerId)
                                        }.distinct()
                                when (if (detailTappable) clicked.size else 0) {
                                    0 -> {
                                        ClickResult.Pass
                                    }

                                    1 -> {
                                        currentOnUserGesture()
                                        onMarkerClick(clicked.single())
                                        ClickResult.Consume
                                    }

                                    else -> {
                                        currentOnUserGesture()
                                        onMarkerGroupClick(clicked)
                                        ClickResult.Consume
                                    }
                                }
                            },
                        )
                    }

                    ClusterSatelliteLayers(
                        source = markerSource,
                        badges = badges,
                        markerIcons = markerIcons,
                        fallbackGlyph = fallbackGlyph,
                        clusterFilter = Feature.has(POINT_COUNT_PROPERTY),
                        opacity = detailOpacity,
                    )

                    ClusterDiscLayer(
                        id = CLUSTER_LAYER_ID,
                        source = markerSource,
                        filter = Feature.has(POINT_COUNT_PROPERTY),
                        countText = Feature[POINT_COUNT_PROPERTY].convertToString(),
                        disc = clusterDiscPainter,
                        opacity = detailOpacity,
                        onClick = { features ->
                            // En fondu sous la pyramide, l'amas laisse le toucher a celle-ci.
                            val cluster = features.firstOrNull()?.takeIf { detailTappable }
                            val pointCount =
                                cluster
                                    ?.properties
                                    ?.get(POINT_COUNT_PROPERTY)
                                    ?.jsonPrimitive
                                    ?.intOrNull
                            val position =
                                (cluster?.geometry as? Point)?.coordinates?.let { coordinates ->
                                    Location(
                                        latitude = coordinates.latitude,
                                        longitude = coordinates.longitude,
                                    )
                                }

                            if (cluster == null || pointCount == null || position == null) {
                                ClickResult.Pass
                            } else {
                                currentOnUserGesture()
                                val sourceHandle = styleMapState?.style?.sources?.get(markerSource)
                                // Etat du SDK, pas le miroir applicatif `camera.position` : ce
                                // dernier n'est reecrit qu'apres un aller-retour par snapshotFlow,
                                // et deux taps rapproches repartiraient du meme zoom perime.
                                val currentZoom =
                                    styleMapState?.cameraPosition?.zoom?.toFloat()
                                        ?: camera.position.zoom
                                clusterTapScope.launch {
                                    val expansionZoom =
                                        sourceHandle?.getClusterExpansionZoom(cluster)?.toFloat()
                                    val targetZoom = clusterTapZoom(currentZoom, expansionZoom)
                                    if (targetZoom != null) {
                                        camera.moveTo(MapCameraPosition(position, targetZoom))
                                        return@launch
                                    }
                                    // L'amas ne se separe plus : ses feuilles se choisissent dans
                                    // une feuille de selection, comme des marqueurs confondus sous
                                    // le doigt. Sans handle (style pas pret), les points confondus
                                    // en coordonnees restent la seule lecture possible.
                                    val leafIds =
                                        sourceHandle
                                            ?.getClusterLeaves(cluster, pointCount.toLong(), 0L)
                                            ?.features
                                            ?.mapNotNull { leaf ->
                                                leaf.properties
                                                    ?.get(MARKER_ID_PROPERTY)
                                                    ?.jsonPrimitive
                                                    ?.longOrNull
                                                    ?.let(::MarkerId)
                                            }?.distinct()
                                            .orEmpty()
                                            .ifEmpty { markers.markerIdsAt(position) }
                                    when (leafIds.size) {
                                        0 -> Unit
                                        1 -> onMarkerClick(leafIds.single())
                                        else -> onMarkerGroupClick(leafIds)
                                    }
                                }
                                ClickResult.Consume
                            }
                        },
                    )

                    // Au-dessus des regroupements du lot detaille, qu'elle remplace en vue large
                    // (#622), et sous les plaques de lieu.
                    if (pyramid != null) {
                        PyramidLayers(
                            pyramid = pyramid,
                            opacity = shownPyramidOpacity,
                            imageCache = imageCache,
                            clusterDisc = clusterDiscPainter,
                            markerIcons = markerIcons,
                            fallbackGlyph = fallbackGlyph,
                            currentZoom = {
                                styleMapState?.cameraPosition?.zoom?.toFloat() ?: camera.position.zoom
                            },
                            onClusterClick = { target ->
                                currentOnUserGesture()
                                camera.moveTo(target)
                            },
                            onTreeClick = { id ->
                                currentOnUserGesture()
                                onPyramidTreeClick(id)
                            },
                        )
                    }

                    // Au-dessus des arbres et de leurs regroupements, sous le point bleu.
                    SitePlaqueLayers(
                        plaques = plaques,
                        markerIcons = markerIcons,
                        fallbackGlyph = fallbackGlyph,
                        onPlaqueClick = onPlaqueClick,
                        onUserGesture = { currentOnUserGesture() },
                    )

                    // Point bleu maison plutot que le composant de localisation du SDK : permission
                    // et fix sont deja geres par core-maps, et `userLocation == null` doit rester
                    // « ne dessine rien », jamais une position par defaut.
                    if (userLocation != null) {
                        val userSource =
                            rememberGeoJsonSource(
                                data =
                                    GeoJsonData.Features(
                                        remember(userLocation) { userLocation.toFeatureCollection() },
                                    ),
                            )
                        CircleLayer(
                            id = USER_LOCATION_LAYER_ID,
                            source = userSource,
                            color = const(USER_LOCATION_COLOR),
                            strokeColor = const(Color.White),
                            radius = const(7.dp),
                            strokeWidth = const(3.dp),
                        )
                    }
                }

            // Deplacements demandes par l'application. `commands` est un flux d'evenements consommes
            // une seule fois : le collecter, plutot que relire un etat, evite de ramener la camera
            // sur la derniere cible a chaque recomposition ou rotation.
            LaunchedEffect(mapState) {
                camera.commands.collect { command ->
                    when (command) {
                        is CameraCommand.MoveTo -> {
                            // Viser un point precis remplace le cadrage : les deux se disputeraient
                            // la camera.
                            pendingFit = null
                            mapState.moveCameraTo(command.target, command.animate)
                        }

                        is CameraCommand.FitBounds -> {
                            animateFit = command.animate
                            pendingFit = command.bounds
                        }
                    }
                }
            }

            // Applique le cadrage courant, et le rejoue si la vue ou ses marges changent.
            LaunchedEffect(mapState) {
                snapshotFlow { Triple(pendingFit, viewportSize, camera.contentPadding) }
                    .collect { (bounds, size, padding) ->
                        if (bounds == null || size.width <= 0.dp || size.height <= 0.dp) return@collect
                        mapState.moveCameraTo(
                            bounds.toCameraPosition(size, padding, layoutDirection),
                            animateFit,
                        )
                    }
            }

            // Retour du SDK vers l'application (geste, fin d'animation). `onCameraMove` n'emet
            // aucune commande, ce qui casse la boucle de feedback avec les blocs ci-dessus.
            //
            // L'origine est rapportee avec la position : un cadrage automatique deplace la camera
            // loin, et sans cette distinction l'application le relirait comme un pan de
            // l'utilisateur. Tout ce qui n'est ni programmatique ni « pas encore bouge » compte
            // comme geste. Si le SDK etiquetait mal un pan, mieux vaut recharger pour rien que
            // cesser de suivre l'utilisateur (#125).
            //
            // `NONE` est la valeur de depart (la camera n'a pas encore bouge) : la traiter en geste
            // ferait un geste fantome au premier snapshot, qui annulerait le cadrage en attente et
            // armerait la persistance de vue avant le moindre doigt sur l'ecran.
            LaunchedEffect(mapState) {
                snapshotFlow { mapState.cameraPosition to mapState.cameraMoveReason }
                    .collect { (position, reason) ->
                        val source =
                            when (reason) {
                                CameraMoveReason.NONE, CameraMoveReason.PROGRAMMATIC -> {
                                    CameraMoveSource.PROGRAMMATIC
                                }

                                else -> {
                                    // L'utilisateur reprend la carte : ne plus rejouer le cadrage
                                    // sous son doigt.
                                    pendingFit = null
                                    currentOnUserGesture()
                                    CameraMoveSource.GESTURE
                                }
                            }
                        camera.onCameraMove(position.toFrux(), source)
                    }
            }

            // Etat de chargement lu en snapshot et non sur `mapState.events` : le flux d'evenements
            // n'a aucun replay et partage sa file avec un evenement par frame rendue, donc un
            // `StyleLoadFailed` du demarrage peut etre perdu. Et une reconciliation ratee (sources,
            // images, couches) pose `Failed` sans emettre d'evenement du tout.
            LaunchedEffect(mapState) {
                snapshotFlow { mapState.style.loadState }
                    .collect { state ->
                        when (state) {
                            is StyleLoadState.Failed -> loadFailure = state.reason ?: "raison inconnue"
                            StyleLoadState.Ready -> loadFailure = null
                            else -> Unit
                        }
                    }
            }

            // Les donnees GeoJSON refusees ne vident pas la carte (la source garde son contenu
            // precedent) : rien a montrer a l'utilisateur, mais une trace pour comprendre des
            // marqueurs qui ne bougent plus.
            LaunchedEffect(mapState) {
                mapState.events.collect { event ->
                    if (event is MapEvent.SourceDataFailed) {
                        Logger.e(
                            tag = "MapLibreFruxMap",
                            message = "donnees GeoJSON refusees par la source ${event.sourceId}",
                            throwable = event.cause,
                        )
                    }
                }
            }

            MaplibreMap(
                modifier = Modifier.fillMaxSize(),
                state = mapState,
                interactions = interactions,
                // Bords que les surcouches de l'ecran recouvrent, pour que le bouton
                // d'attribution ne se glisse pas sous la navigation basse ni sous la colonne
                // grand ecran. En union avec les insets systeme, jamais a leur place.
                contentWindowInsets =
                    WindowInsets.safeDrawing.union(
                        attributionPadding(
                            camera.contentPadding,
                            camera.chromeInsets,
                            layoutDirection,
                            camera.bottomOverlayInset,
                        ).toWindowInsets(layoutDirection),
                    ),
                // `showOrnaments = false` signifie « carte imbriquée » (cf. FruxMap) : elle vit dans
                // un écran qui s'anime, d'où un rendu qui suit les transitions.
                uiOptions =
                    remember(showOrnaments, loadColor) {
                        mapUiOptions(embedded = !showOrnaments, loadColor = loadColor)
                    },
                // Seul ornement sur une carte plein ecran : le bouton (i) du SDK, en Compose depuis
                // maplibre-compose 0.16 (donc identique sur les quatre cibles), deploye au
                // chargement et replie au premier geste. Son panneau reunit les tuiles (lues dans le
                // document de style), les mentions de l'ecran appelant et MapLibre. Pas le wordmark
                // MapLibre de `MapOverlay.AttributionOnly` : rien ne l'impose (licence BSD, a la
                // difference de Google Maps ou Mapbox) et il se disputait le bas de l'ecran avec les
                // controles de l'ecran carte. Ni echelle ni boussole : l'echelle suit la locale de
                // l'appareil (pieds en anglais) et aucun parcours de cueillette n'a besoin de mesurer
                // une distance a l'ecran. La boussole ne servait qu'a revenir au nord apres une
                // rotation, desormais interdite (cf. `interactions` ci-dessus).
                // Carte imbriquee (showOrnaments = false) : rien du tout, ce serait du bruit a
                // cette taille, et l'attribution est redessinee plus bas.
                overlay = {
                    if (showOrnaments) {
                        ExpandingAttributionButton(
                            modifier = Modifier.align(Alignment.BottomEnd),
                            expandedContent = { styleAttributions, textStyle ->
                                AttributionLinks(
                                    attributions = styleAttributions + attributions + MAPLIBRE_ATTRIBUTION,
                                    textStyle = textStyle,
                                )
                            },
                        )
                    }
                },
            )

            // Bulle ancree au-dessus du marqueur selectionne (#196 lot 5).
            val selectedMarker = markers.find { it.selected }
            // Presence de la vue rendue, derivee et non lue directement : `viewport` est remplace a
            // chaque frame de camera, et le lire en composition recomposerait tout ce bloc a chaque
            // frame de pan. Seul son passage de null a non-null doit compter.
            val hasViewport by remember(mapState) { derivedStateOf { mapState.viewport != null } }
            if (selectedMarker != null && markerCallout != null && hasViewport) {
                // Taille de la bulle, connue seulement apres sa premiere mesure : au tout premier
                // frame elle vaut Zero, ce qui la place a un point provisoire (coin superieur gauche
                // du marqueur) avant que `onSizeChanged` ne la recale. Assume, cf. rapport.
                var calloutSize by remember { mutableStateOf(IntSize.Zero) }
                Box(
                    modifier =
                        Modifier
                            .offsetModifier {
                                // Lu ici, en phase de layout (et non capture dans une `val` Compose
                                // classique en dehors) : la dependance doit re-layouter a chaque
                                // frame de pan, pas recomposer la bulle.
                                mapState.cameraPosition
                                val screen =
                                    mapState.screenLocationFromPosition(
                                        selectedMarker.position.toPosition(),
                                    ) ?: return@offsetModifier IntOffset.Zero
                                IntOffset(
                                    x = screen.x.roundToPx() - calloutSize.width / 2,
                                    // Derive de la taille reellement occupee par le marqueur
                                    // selectionne (bord compris), et non d'une valeur en dur : la
                                    // bulle mordait la pastille des que le marqueur grossissait.
                                    y =
                                        screen.y.roundToPx() - calloutBottomOffset().roundToPx() -
                                            calloutSize.height,
                                )
                            }.onSizeChanged { calloutSize = it },
                ) {
                    markerCallout(selectedMarker.id)
                }
            }
        }

        // Attribution des tuiles : obligation du fournisseur, pas une decoration. Quand les
        // ornements sont coupes (carte imbriquee via showOrnaments), c'est la seule attribution
        // affichee, donc un vrai lien.
        if (!showOrnaments) {
            MapAttributionLink(
                attribution = mapAttribution(),
                // `contentPadding` decrit les bords que les surcouches de l'ecran recouvrent : sans
                // lui, l'attribution se glisserait sous la navigation basse, donc invisible.
                // `chromeInsets` ajoute la colonne grand écran, que l'écran carte ne connaît pas.
                // En bas a gauche, la ou MapLibre place son propre logo : c'est le coin que les
                // ecrans laissent libre (le bouton d'ajout et le recentrage occupent l'autre).
                modifier =
                    Modifier
                        .align(Alignment.BottomStart)
                        .padding(
                            attributionPadding(
                                camera.contentPadding,
                                camera.chromeInsets,
                                layoutDirection,
                                camera.bottomOverlayInset,
                            ),
                        ),
            )
        }

        loadFailure?.let { reason ->
            MapLoadFailure(
                reason = reason,
                onRetry = {
                    loadFailure = null
                    retryFrom = camera.position
                    attempt++
                },
                modifier = Modifier.align(Alignment.Center),
            )
        }
    }
}

/** Bords decrits en marges d'ecran, pour la surcouche d'ornements du SDK. */
private fun PaddingValues.toWindowInsets(layoutDirection: LayoutDirection): WindowInsets =
    WindowInsets(
        left =
            if (layoutDirection == LayoutDirection.Ltr) {
                calculateStartPadding(layoutDirection)
            } else {
                calculateEndPadding(layoutDirection)
            },
        top = calculateTopPadding(),
        right =
            if (layoutDirection == LayoutDirection.Ltr) {
                calculateEndPadding(layoutDirection)
            } else {
                calculateStartPadding(layoutDirection)
            },
        bottom = calculateBottomPadding(),
    )

/**
 * Zoom vise au tap sur un regroupement, ou `null` quand aucun zoom ne le separera (#398).
 *
 * [expansionZoom] est le premier zoom entier ou la source eclate l'amas ; elle rend un cran
 * au-dela de son dernier zoom regroupe pour un amas qui ne se separe jamais (points confondus ou
 * plus proches que le rayon au zoom maximal), et `null` quand le style n'est pas pret. Sans
 * reponse exploitable, un pas fixe garde le tap utile. Jamais de retour en arriere, meme si la
 * camera est deja plus loin que le zoom d'eclatement.
 */
internal fun clusterTapZoom(
    currentZoom: Float,
    expansionZoom: Float?,
): Float? =
    when {
        expansionZoom == null || expansionZoom <= 0f -> currentZoom + CLUSTER_FALLBACK_ZOOM_STEP
        expansionZoom > CLUSTER_MAX_ZOOM -> null
        else -> expansionZoom
    }?.coerceIn(currentZoom, MAX_MAP_ZOOM)

private const val CLUSTER_FALLBACK_ZOOM_STEP = 2f

/**
 * Mention d'attribution cliquable, dessinee par l'application.
 *
 * Deliberement discrete mais toujours au-dessus de la carte : les conditions des fournisseurs de
 * tuiles imposent une attribution visible et un acces aux credits, ce que les ornements du SDK ne
 * peuvent pas assurer sur une carte imbriquee ou ils sont coupes.
 */
@Composable
private fun MapAttributionLink(
    attribution: MapAttribution,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    Surface(
        modifier = modifier.padding(4.dp),
        color = MaterialTheme.colorScheme.surface.copy(alpha = 0.75f),
        contentColor = MaterialTheme.colorScheme.onSurface,
        shape = MaterialTheme.shapes.extraSmall,
    ) {
        Text(
            text = attribution.label,
            style = MaterialTheme.typography.labelSmall,
            modifier =
                Modifier
                    .clickable { uriHandler.openUri(attribution.url) }
                    .padding(horizontal = 6.dp, vertical = 2.dp),
        )
    }
}

@Composable
private fun MapLoadFailure(
    reason: String,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.padding(24.dp),
        color = MaterialTheme.colorScheme.errorContainer,
        contentColor = MaterialTheme.colorScheme.onErrorContainer,
        shape = MaterialTheme.shapes.medium,
        tonalElevation = 4.dp,
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(text = "Carte indisponible", style = MaterialTheme.typography.titleSmall)
            Text(text = reason, style = MaterialTheme.typography.bodySmall)
            TextButton(onClick = onRetry) { Text("Réessayer") }
        }
    }
}

internal fun List<MapMarker>.toFeatureCollection(
    badges: BadgeRegistry? = null,
): FeatureCollection<Point, JsonObject?> =
    FeatureCollection(
        features =
            map { marker ->
                GeoJsonFeature(
                    geometry = Point(marker.position.toPosition()),
                    properties =
                        buildJsonObject {
                            put(MARKER_ID_PROPERTY, marker.id.value)
                            put(MARKER_STYLE_PROPERTY, markerStyleKey(marker))
                            put(MARKER_ALPHA_PROPERTY, marker.alpha)
                            put(MARKER_SELECTED_PROPERTY, marker.selected)
                            badges?.indexOf(marker.badge)?.let { put(BADGE_FEATURE_PROPERTY, it) }
                        },
                )
            },
    )

private fun Location.toFeatureCollection(): FeatureCollection<Point, JsonObject?> =
    FeatureCollection(GeoJsonFeature(geometry = Point(toPosition()), properties = null))

internal fun Location.toPosition(): Position = Position(longitude = longitude, latitude = latitude)

private fun MapCameraPosition.toMaplibre() = CameraPosition(target = target.toPosition(), zoom = zoom.toDouble())

private suspend fun MapState.moveCameraTo(
    target: MapCameraPosition,
    animate: Boolean,
) {
    if (animate) {
        // Un geste pendant l'animation la reprend : voir survivingCameraTakeover (#500).
        survivingCameraTakeover { animateCameraPosition(target.toMaplibre(), duration = MOVE_DURATION) }
    } else {
        setCameraPosition(target.toMaplibre())
    }
}

private fun CameraPosition.toFrux(): MapCameraPosition =
    MapCameraPosition(
        target = Location(latitude = target.latitude, longitude = target.longitude),
        zoom = zoom.toFloat(),
    )

/** MapLibre lit les couleurs comme des chaines CSS ; l'alpha des teintes de marqueur est ignore. */
internal fun Color.toCssHex(): String = "#" + (toArgb() and 0xFFFFFF).toString(16).padStart(6, '0')
